import { Form, Formik } from "formik";
import React, { FC, ReactElement, useState } from "react";
import { toast } from "react-toastify";
import { ENROLL_TYPE } from "../../../constants/command-status";
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { EnrolmentDevice } from "../../../models/EnrolmentDevices";
import { ExecuteCommand } from "../../../models/ExecuteCommand";
import { MultiSelectOption } from "../../../models/MultiSelectOption";
import { Tag } from "../../../models/Tag";
import WizardSideBarStep from "../../../models/WizardSideBarStep";
import { saveCredential } from "../../../services/credentialsService";
import { enrolFace } from "../../../services/executeCommandService";
import { getErrorMessage } from "../../../services/utilities/ServiceError";
import { buildCardData, generateNewRandomPAC } from "../../../utilities/CredentialUtil";
import { useDialog } from "../../dialog/DialogContext";
import Wizard from "../../wizard/wizard";
import ChooseCard from "../choose-card";
import DetailsForm from '../details-form';
import SummaryForm from "./summary-form";

interface Props {
    defaultContent: ReactElement[];
    defaultSteps: WizardSideBarStep[];
    hasTags: boolean;
    onBack: (event: any) => void;
    onClose: () => void;
    cardInfo: Tag;
    updateBadge: (badge: number) => void;
    masterId: number | undefined;
}

const EnrolQrCodeWizard: FC<Props> = ({ defaultSteps, cardInfo, onClose, defaultContent, onBack, updateBadge, hasTags, masterId }) => {
    const { translate } = useLocale();
    const [step, setStep] = useState<number>(1);
    const [image, setImage] = useState<any>('');
    const dialog = useDialog();
    const [newCard, setNewCard] = useState<Tag>(cardInfo);
    const [isNewCard, setIsNewCard] = useState<boolean>(false);
    const [selectedTag, setSelectedTag] = useState<MultiSelectOption>({ value: "", label: "" });
    const enrolmentDevice = new EnrolmentDevice(translate);

    const onLocalNext = (event) => {
        if(event.code) {
            setSelectedTag({ ...selectedTag, label: event.code });    
        }
        setStep(step + 1);
    }

    const onLocalBack = (event?: any) => {
        if (step === 1) {
            onBack(event);
            return;
        }
        
        setStep(step - 1);
    }

    const onLocalSave = async (values) => {
        try {

            let newTag: any = undefined;
            if (isNewCard) {
                newTag = await saveCredential({ ...buildCardData(values, translate), masterId: masterId });
            }

            const command: ExecuteCommand = {
                cmd: ENROLL_TYPE.ENROLL,
                cmdSubType: ENROLL_TYPE.ENROLL_QR_CODE,
                tagId: newTag ? newTag.content.data.id : selectedTag.value,
                image: image
            }

            enrolFace(command).then((result) => { //we are using this method to store QR code
                if (result.success == true && command.cmdSubType === ENROLL_TYPE.ENROLL_QR_CODE) {
                    toast.success(translate.byKey(TranslationKeys.credentialSaved));
                    updateBadge(1);
                    onClose();
                }

            }).catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
            })
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
        }
    }

    const downloadImage = () => {
        const img = new Image();
        img.src = image;

        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                console.debug('base64Image is undefined');
                return
            }
            const padding = 20; // Adjust the padding value as needed

            canvas.width = img.width + 2 * padding;
            canvas.height = img.height + 2 * padding;

            ctx.fillStyle = 'white'; // Set the padding color
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            ctx.drawImage(img, padding, padding);

            const downloadLink = document.createElement('a');
            downloadLink.href = canvas.toDataURL('image/png');
            downloadLink.download = 'QR_Code.png';
            downloadLink.click();

        };
    };

    const onChooseCardNext = (e) => {
        if (e.value && e.value === enrolmentDevice.MANUAL_CODE_TYPE.value) {
            setNewCard({
                ...e.values,
                code: cardInfo.code,
                typeId: cardInfo.typeId,
                startDate: undefined,
                startTime: '',
                expiryDate: undefined,
                expiryTime: '',
            });
            setIsNewCard(true);
        } else if (e.value && e.value === enrolmentDevice.RANDOM_PAC.value) {
            const tag: Tag = generateNewRandomPAC(newCard, translate)
            setNewCard({
                ...e.values,
                code: tag.code,
                tagDisplay: tag.code,
                type: tag.type,
                typeId: tag.typeId,
                startDate: undefined,
                startTime: '',
                expiryDate: undefined,
                expiryTime: '',
            });
            setSelectedTag({ ...selectedTag, label: tag.code })
            setIsNewCard(true);
        } else {
            setSelectedTag({ value: e.value, label: e.code })
            setStep(step + 1);
        }
    }

    const steps: WizardSideBarStep[] = [
        ...defaultSteps,
        {
            title: translate.byKey(TranslationKeys.card),
            description: selectedTag.label,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: '',
            substeps: []
        }
    ];

    const content = [
        ...defaultContent,
        isNewCard ?
            <DetailsForm
                onBack={(e) => { setIsNewCard(false); }}
                onNext={onLocalNext}
                cardInfo={newCard} /> :
            <ChooseCard
                masterId={masterId}
                onBack={onLocalBack}
                onNext={onChooseCardNext} />,
        <SummaryForm
            taskDescription={steps[0].description}
            onBack={(event) => onLocalBack(event)}
            isEdit={false}
            cardCode={selectedTag.label}
            setImage={setImage}
            downloadQRCode={downloadImage}
        />
    ]

    return <>
        <Formik
            enableReinitialize={true}
            initialValues={newCard}
            onSubmit={onLocalSave}>
            <Form>
                <Wizard name={translate.byKey(TranslationKeys.enrollCredential)}
                    step={step}
                    onClose={() => onClose()}
                    sidebarSteps={steps}>
                    {content}
                </Wizard>
            </Form>
        </Formik>
    </>
}

export default EnrolQrCodeWizard;